import { useCallback, useState } from 'react'
import { Upload, X, FileText, Image, FileSpreadsheet, File } from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  EXTENSIONES_PERMITIDAS,
  LIMITE_ARCHIVO_MB,
  esArchivoPermitido,
  esTamanoPermitido,
  formatFileSize,
} from '@/types/documentos'

interface DropZoneCompactProps {
  file: File | null
  onFileSelected: (file: File | null) => void
  disabled?: boolean
  className?: string
  accept?: string
}

function getFileIcon(file: File) {
  const type = file.type
  if (type.startsWith('image/')) return <Image className="h-5 w-5 text-blue-500" />
  if (type === 'application/pdf') return <FileText className="h-5 w-5 text-red-500" />
  if (type.includes('spreadsheet') || type.includes('excel'))
    return <FileSpreadsheet className="h-5 w-5 text-green-500" />
  return <File className="h-5 w-5 text-muted-foreground" />
}

export function DropZoneCompact({
  file,
  onFileSelected,
  disabled = false,
  className,
  accept = EXTENSIONES_PERMITIDAS.join(','),
}: DropZoneCompactProps) {
  const [isDragging, setIsDragging] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const validateFile = useCallback((f: File): string | null => {
    if (!esArchivoPermitido(f)) {
      return `Tipo de archivo no permitido`
    }
    if (!esTamanoPermitido(f)) {
      return `El archivo excede ${LIMITE_ARCHIVO_MB}MB`
    }
    return null
  }, [])

  const handleFile = useCallback(
    (f: File | null) => {
      if (!f || disabled) {
        onFileSelected(null)
        setError(null)
        return
      }

      const validationError = validateFile(f)
      if (validationError) {
        setError(validationError)
        return
      }

      setError(null)
      onFileSelected(f)
    },
    [disabled, validateFile, onFileSelected]
  )

  const handleDragOver = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      if (!disabled) {
        setIsDragging(true)
      }
    },
    [disabled]
  )

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
  }, [])

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      setIsDragging(false)
      const files = e.dataTransfer.files
      if (files.length > 0) {
        handleFile(files[0])
      }
    },
    [handleFile]
  )

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files
      if (files && files.length > 0) {
        handleFile(files[0])
      }
      e.target.value = ''
    },
    [handleFile]
  )

  const handleRemove = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()
      onFileSelected(null)
      setError(null)
    },
    [onFileSelected]
  )

  // Si hay un archivo seleccionado, mostrar la vista compacta con el archivo
  if (file) {
    return (
      <div className={cn('space-y-2', className)}>
        <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg border border-border">
          {getFileIcon(file)}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">{file.name}</p>
            <p className="text-xs text-muted-foreground">{formatFileSize(file.size)}</p>
          </div>
          <button
            type="button"
            onClick={handleRemove}
            disabled={disabled}
            className="p-1.5 hover:bg-muted rounded-md transition-colors disabled:opacity-50"
          >
            <X className="h-4 w-4 text-muted-foreground" />
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className={cn('space-y-2', className)}>
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={cn(
          'relative border-2 border-dashed rounded-lg p-4 text-center transition-colors',
          isDragging
            ? 'border-primary bg-primary/5'
            : 'border-muted-foreground/25 hover:border-muted-foreground/50',
          disabled && 'opacity-50 cursor-not-allowed',
          !disabled && 'cursor-pointer'
        )}
      >
        <input
          type="file"
          onChange={handleInputChange}
          disabled={disabled}
          accept={accept}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed"
        />

        <div className="flex items-center justify-center gap-3">
          <Upload
            className={cn(
              'h-5 w-5 transition-colors',
              isDragging ? 'text-primary' : 'text-muted-foreground'
            )}
          />
          <div className="text-sm">
            <span className="font-medium text-primary">Seleccionar archivo</span>
            <span className="text-muted-foreground hidden sm:inline"> o arrastrar</span>
          </div>
        </div>
      </div>

      {error && (
        <p className="text-xs text-destructive">{error}</p>
      )}
    </div>
  )
}
